import type { Transaction } from 'types/transaction';
import { ROUTES } from 'utils/constants';

/** Sales, CRJ, POS, etc. — edit/void only from the originating module. */
export function isTransactionManagedByExternalModule(
  transaction: Pick<Transaction, 'sourceType'>,
): boolean {
  return transaction.sourceType != null && transaction.sourceType !== 'receipt';
}

export const EXTERNAL_MODULE_TRANSACTION_ACTION_TITLE =
  'Cannot modify transaction that originated from another module';

const SOURCE_TYPE_LABELS: Record<string, string> = {
  cash_receipts_journal: 'Cash Receipts Journal',
  cash_disbursements_journal: 'Cash Disbursements Journal',
  sales: 'Sales',
  disbursement: 'Disbursements',
  pos: 'POS',
  inventory: 'Inventory',
  ap_bill: 'Bills',
  ap_bill_payment: 'Bills',
  ap_bill_fx_revaluation: 'FX Revaluations',
  capital: 'Capitals',
  equipment: 'Equipment',
  ar_invoice: 'Invoices',
  ar_invoice_payment: 'Invoices',
};

export function getExternalModuleSourceLabel(
  sourceType: string | null | undefined,
): string | null {
  if (!sourceType || sourceType === 'receipt') {
    return null;
  }
  return SOURCE_TYPE_LABELS[sourceType] ?? sourceType.replace(/_/g, ' ');
}

export function getExternalModuleTransactionBlockedMessage(
  sourceType: string | null | undefined,
): string {
  const label = getExternalModuleSourceLabel(sourceType);
  if (!label) {
    return EXTERNAL_MODULE_TRANSACTION_ACTION_TITLE;
  }
  return `This transaction was created in ${label}. Edit or void it from that module instead.`;
}

export function getCashReceiptsJournalEntryViewPath(entryId: string): string {
  return `${ROUTES.CASH_RECEIPTS_JOURNAL.ROOT}/${entryId}`;
}

export function getCashReceiptsJournalEntryEditPath(entryId: string): string {
  return `${ROUTES.CASH_RECEIPTS_JOURNAL.ROOT}/${entryId}/edit`;
}

export function getCashDisbursementJournalEntryViewPath(
  entryId: string,
): string {
  return `${ROUTES.CASH_DISBURSEMENT_JOURNAL.ROOT}/${entryId}`;
}

export function getCashDisbursementJournalEntryEditPath(
  entryId: string,
): string {
  return `${ROUTES.CASH_DISBURSEMENT_JOURNAL.ROOT}/${entryId}/edit`;
}

export function isCashDisbursementJournalTransaction(
  transaction: Pick<Transaction, 'sourceType' | 'sourceEntryId'>,
): boolean {
  return (
    transaction.sourceType === 'cash_disbursements_journal' &&
    Boolean(transaction.sourceEntryId?.trim())
  );
}

export function isCashReceiptsJournalTransaction(
  transaction: Pick<Transaction, 'sourceType' | 'sourceEntryId'>,
): boolean {
  return (
    transaction.sourceType === 'cash_receipts_journal' &&
    Boolean(transaction.sourceEntryId?.trim())
  );
}

export function getApBillDetailsPath(billId: string): string {
  return ROUTES.BILLS_LEDGER.DETAIL(billId);
}

export function isApBillLedgerTransaction(
  transaction: Pick<Transaction, 'sourceType' | 'sourceEntryId'>,
): boolean {
  const billId = transaction.sourceEntryId?.trim();
  if (!billId) {
    return false;
  }
  return (
    transaction.sourceType === 'ap_bill' ||
    transaction.sourceType === 'ap_bill_payment'
  );
}

export function getCapitalEntryPath(entryId?: string): string {
  const id = entryId?.trim();
  if (!id) {
    return ROUTES.CAPITALS.ROOT;
  }
  return ROUTES.CAPITALS.DETAIL(id);
}

export function isCapitalTransaction(
  transaction: Pick<Transaction, 'sourceType' | 'sourceEntryId'>,
): boolean {
  return (
    transaction.sourceType === 'capital' &&
    Boolean(transaction.sourceEntryId?.trim())
  );
}

export function getEquipmentDetailsPath(equipmentId: string): string {
  return ROUTES.EQUIPMENT.DETAIL(equipmentId);
}

export function getEquipmentDepreciationDetailPath(entryId: string): string {
  return ROUTES.EQUIPMENT.DEPRECIATION_DETAIL(entryId);
}

export function isEquipmentTransaction(
  transaction: Pick<Transaction, 'sourceType' | 'sourceEntryId'>,
): boolean {
  return (
    transaction.sourceType === 'equipment' &&
    Boolean(transaction.sourceEntryId?.trim())
  );
}

export function isEquipmentDepreciationTransaction(
  transaction: Pick<Transaction, 'sourceType' | 'sourceEntryId'>,
): boolean {
  return (
    transaction.sourceType === 'equipment_depreciation' &&
    Boolean(transaction.sourceEntryId?.trim())
  );
}

export function getApBillFxRevaluationDetailPath(entryId: string): string {
  return ROUTES.FX_REVALUATIONS.DETAIL(entryId);
}

export function isApBillFxRevaluationTransaction(
  transaction: Pick<Transaction, 'sourceType' | 'sourceEntryId'>,
): boolean {
  return (
    transaction.sourceType === 'ap_bill_fx_revaluation' &&
    Boolean(transaction.sourceEntryId?.trim())
  );
}

export function getArInvoiceDetailsPath(invoiceId: string): string {
  return ROUTES.AR_INVOICES.DETAIL(invoiceId);
}

export function getSourceEntryDetailPath(
  sourceType: string | null | undefined,
  sourceEntryId: string | null | undefined,
): string | null {
  const type = sourceType?.trim();
  const entryId = sourceEntryId?.trim();
  if (!type || !entryId) {
    return null;
  }

  switch (type) {
    case 'cash_receipts_journal':
      return getCashReceiptsJournalEntryViewPath(entryId);
    case 'cash_disbursements_journal':
      return getCashDisbursementJournalEntryViewPath(entryId);
    case 'ap_bill':
    case 'ap_bill_payment':
      return getApBillDetailsPath(entryId);
    case 'ap_bill_fx_revaluation':
      return getApBillFxRevaluationDetailPath(entryId);
    case 'ar_invoice':
    case 'ar_invoice_payment':
      return getArInvoiceDetailsPath(entryId);
    case 'equipment':
      return getEquipmentDetailsPath(entryId);
    case 'equipment_depreciation':
      return getEquipmentDepreciationDetailPath(entryId);
    case 'capital':
      return getCapitalEntryPath(entryId);
    default:
      return null;
  }
}

export function isArInvoiceLedgerTransaction(
  transaction: Pick<Transaction, 'sourceType' | 'sourceEntryId'>,
): boolean {
  const invoiceOrPaymentId = transaction.sourceEntryId?.trim();
  if (!invoiceOrPaymentId) {
    return false;
  }
  return (
    transaction.sourceType === 'ar_invoice' ||
    transaction.sourceType === 'ar_invoice_payment'
  );
}
